import { z } from 'zod';
import { init } from 'zod-empty';
import { CompiledField, ConversionFormStep, ConversionFormStepField, ConversionFormStepFieldTypename, FormType } from '../types';
import { makeFieldSchema } from './fieldValidations';
import { makeFieldId, makeStepId } from './makeFieldId';
export type FormManifest = {
  form: FormType | null;
  defaultValues: Record<string, unknown>;
  watchFields: string[];
  formSchema: z.ZodObject<Record<string, z.ZodTypeAny>>;
  stepSchema: Map<string, z.ZodObject<Record<string, z.ZodTypeAny>>>;
  flattenedFields: CompiledField<ConversionFormStepFieldTypename>[];
  fieldByKey: Map<string, CompiledField<ConversionFormStepFieldTypename>>;
  fieldsBySection: Map<string, CompiledField<ConversionFormStepFieldTypename>[]>;
  /** Field registration IDs per step key (same order as fieldsBySection). */
  fieldKeysBySection: Map<string, string[]>;
  steps: StepDefinition[];
};
export type StepDefinition = {
  _key: string;
  name?: string;
  slug: string;
};

// type CompiledField = {
//   id: string
//   sectionKey: string
//   isConditional: boolean
// }

type ReducerState = {
  sectionKey: string;
  schemaObj: Record<string, z.ZodTypeAny>;
  watchFields: Set<string>;
  flattenedFields: CompiledField<ConversionFormStepFieldTypename>[];
  fieldByKey: Map<string, CompiledField<ConversionFormStepFieldTypename>>;
  fieldsBySection: Map<string, CompiledField<ConversionFormStepFieldTypename>[]>;
  fieldKeysBySection: Map<string, string[]>;
  steps: StepDefinition[];
  stepSchemaObjs: Map<string, Record<string, z.ZodTypeAny>>;
};
export const initialManifest: FormManifest = {
  form: null,
  defaultValues: {},
  watchFields: [],
  formSchema: z.object({}),
  stepSchema: new Map(),
  flattenedFields: [],
  fieldByKey: new Map(),
  fieldsBySection: new Map(),
  fieldKeysBySection: new Map(),
  steps: []
};
export function makeManifest(form: FormType): FormManifest | void {
  if (!form || !form.steps) {
    return console.error(`No form data provided for form.`);
  }
  const {
    schemaObj,
    watchFields,
    flattenedFields,
    fieldByKey,
    fieldsBySection,
    fieldKeysBySection,
    steps,
    stepSchemaObjs
  } = (form.steps as ConversionFormStep[]).reduce<ReducerState>(reducer, {
    schemaObj: {},
    watchFields: new Set(),
    flattenedFields: [],
    fieldByKey: new Map(),
    fieldsBySection: new Map(),
    fieldKeysBySection: new Map(),
    steps: [],
    sectionKey: '',
    stepSchemaObjs: new Map()
  });
  const formSchema = z.object(schemaObj as Record<string, z.ZodTypeAny>);
  const stepSchema = new Map(Array.from(stepSchemaObjs.entries()).map(([key, value]) => [key, z.object(value as Record<string, z.ZodTypeAny>)]));
  const defaultValues = init(formSchema);
  return {
    form,
    formSchema,
    defaultValues,
    watchFields: Array.from(watchFields),
    flattenedFields,
    fieldByKey,
    fieldsBySection,
    fieldKeysBySection,
    steps,
    stepSchema
  };
}
function reducer<T extends ReducerState>(state: T, section: ConversionFormStep, index: number): T {
  const id = makeStepId(section.name ?? `step-${index}`);
  state.sectionKey = id;
  state.steps.push({
    _key: id,
    name: section.name ?? `Step ${index + 1}`,
    slug: id
  });
  const fields = section.fields?.filter(isStepField) ?? [];
  return fields.reduce<T>(processField, state);
}
function isStepField(field: ConversionFormStepField | null | undefined): field is ConversionFormStepField {
  return field != null;
}
function processField<T extends ReducerState>(state: T, field: ConversionFormStepField, index: number): T {
  // if (isConditionalFieldType(field)) {
  //   const result = processConditionalFieldBlock(state, field)
  //   return result ?? state
  // }
  const id = makeFieldId(state.sectionKey, field.label ?? `field-${index}`);
  const schema = makeFieldSchema(field);
  const stepSchema = state.stepSchemaObjs.get(state.sectionKey!);
  if (schema) {
    state.schemaObj[id] = schema;
    const compiled = compileField(id, field, state.sectionKey!, false);
    state.stepSchemaObjs.set(state.sectionKey!, {
      ...(stepSchema ?? {}),
      [id]: schema
    });
    state.flattenedFields.push(compiled);
    state.fieldByKey.set(id, compiled);
    state.fieldsBySection.set(state.sectionKey!, [...(state.fieldsBySection.get(state.sectionKey!) ?? []), compiled]);
    state.fieldKeysBySection.set(state.sectionKey!, [...(state.fieldKeysBySection.get(state.sectionKey!) ?? []), id]);
  }
  return state;
}
function compileField(id: string, field: ConversionFormStepField, sectionKey: string, isConditional: boolean): CompiledField<ConversionFormStepFieldTypename> {
  return {
    id,
    ...field,
    sectionKey,
    isConditional
  };
}